import React, { useState, useEffect } from 'react';
import API from '../../Utils/api';
import Table from '../Table/Table';
import styles from './PreviewWorkout.module.css';
import { toast } from 'react-toastify';
import { calculateProgramItemTime, formatDuration } from '../../Utils/programTiming';

const SET_TYPES_DISPLAY = {
    warm_up: 'Warm Up',
    pre_set: 'Pre Set',
    drill_set: 'Drill Set',
    main_set: 'Main Set',
    cool_down: 'Cool Down'
};

const SWIMMING_STYLES_DISPLAY = {
    freestyle: 'Free Style',
    backStroke: 'Back Stroke',
    breastStroke: 'Breast Stroke',
    butterfly: 'Butterfly',
    individualMedley: 'Individual Medley'
};

const SubItemCell = ({ children, isSubItem }) => {
    if (isSubItem) {
        return <div style={{ color: '#666', fontWeight: 'normal' }}>{children}</div>;
    }
    return <div style={{ color: '#333', fontWeight: 'bold' }}>{children}</div>;
};

const flattenProgramItems = items => {
    if (!items) return [];
    const flattened = [];
    items.forEach(item => {
        flattened.push(item);
        if (item.subItems && item.subItems.length > 0) {
            item.subItems.forEach(subItem => {
                flattened.push({
                    ...subItem,
                    _isSubItem: true,
                    _parentId: item._id
                });
            });
        }
    });
    return flattened;
};

const PreviewWorkout = ({ isOpen, onClose, workout, athleteCSS, athleteMSS }) => {
    const [programSnapshot, setProgramSnapshot] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (workout?.programGroup) {
                loadProgramSnapshot();
            } else {
                setProgramSnapshot(null);
                setLoading(false);
            }
        }
        // Loads only when the modal opens or the workout changes; loadProgramSnapshot is recreated every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, workout]);

    const loadProgramSnapshot = async () => {
        if (!workout?.programGroup) return;

        setLoading(true);
        try {
            const { data } = await API.getProgramSnapshot(workout.programGroup, workout._id);
            setProgramSnapshot(data);
        } catch (error) {
            toast.error('Failed to load workout program');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const flattenedProgramItems = flattenProgramItems(programSnapshot?.items || []);
    const parentSetLookup = new Map(
        flattenedProgramItems
            .filter(item => !item._isSubItem)
            .map(item => [item._id, item.set || 1])
    );

    const programItemColumns = [
        {
            Header: 'Set Type',
            accessor: 'setType',
            width: 100,
            Cell: ({ value, row }) =>
                row.original._isSubItem ? null : SET_TYPES_DISPLAY[value] || '-'
        },
        {
            Header: 'Set',
            accessor: 'set',
            width: 60,
            Cell: ({ value, row }) => (row.original._isSubItem ? null : value || '-')
        },
        {
            Header: 'Reps',
            accessor: 'reps',
            width: 60,
            Cell: ({ value, row }) => (
                <SubItemCell isSubItem={row.original._isSubItem}>{value || '-'}</SubItemCell>
            )
        },
        {
            Header: 'Distance (m)',
            accessor: 'distance',
            width: 100,
            Cell: ({ value, row }) => (
                <SubItemCell isSubItem={row.original._isSubItem}>
                    {value ? `${value} m` : '-'}
                </SubItemCell>
            )
        },
        {
            Header: 'Style',
            accessor: 'swimmingStyle',
            width: 120,
            Cell: ({ value, row }) => (
                <SubItemCell isSubItem={row.original._isSubItem}>
                    {SWIMMING_STYLES_DISPLAY[value] || '-'}
                </SubItemCell>
            )
        },
        {
            Header: 'Zone',
            accessor: 'zones',
            width: 70,
            Cell: ({ value, row }) => (
                <SubItemCell isSubItem={row.original._isSubItem}>
                    {value ? value.toUpperCase() : '-'}
                </SubItemCell>
            )
        },
        {
            Header: 'Swim Time',
            id: 'swimmingTime',
            width: 100,
            accessor: (row) => {
                const times = calculateProgramItemTime(row, {
                    parentSet: row._isSubItem ? parentSetLookup.get(row._parentId) : undefined,
                    css: athleteCSS,
                    mss: athleteMSS
                });

                return formatDuration(times.swimming);
            },
            Cell: ({ value, row }) => (
                <SubItemCell isSubItem={row.original._isSubItem}>{value}</SubItemCell>
            )
        },
        {
            Header: 'Comments',
            accessor: 'comment',
            width: 150,
            Cell: ({ value, row }) => (
                <SubItemCell isSubItem={row.original._isSubItem}>
                    <div style={{
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {value || '-'}
                    </div>
                </SubItemCell>
            )
        },
        {
            Header: 'Rep Rest (sec)',
            accessor: 'repsRest',
            width: 90,
            Cell: ({ value, row }) => (
                <SubItemCell isSubItem={row.original._isSubItem}>
                    {value ? `${value} s` : '-'}
                </SubItemCell>
            )
        },
        {
            Header: 'Set Rest (sec)',
            accessor: 'setRest',
            width: 90,
            Cell: ({ value, row }) => (
                <SubItemCell isSubItem={row.original._isSubItem}>
                    {value ? `${value} s` : '-'}
                </SubItemCell>
            )
        },
        {
            Header: 'After Rest (sec)',
            accessor: 'afterSetRest',
            width: 100,
            Cell: ({ value, row }) => (
                <SubItemCell isSubItem={row.original._isSubItem}>
                    {value ? `${value} s` : '-'}
                </SubItemCell>
            )
        }
    ];

    return (
        <div className={styles.previewWorkout}>
            {loading ? (
                <div className={styles.loading}>Loading workout program...</div>
            ) : (
                programSnapshot && (
                    <>
                        <div className={styles.workoutInfo}>
                            <div className={styles.workoutHeader}>
                                <h3>{programSnapshot.program.name}</h3>
                                <p className={styles.workoutType}>{programSnapshot.program.type}</p>
                                <p className={styles.snapshotDate}>
                                    Assigned:
                                    {new Date(programSnapshot.snapshotDate).toLocaleDateString(
                                        'el-GR'
                                    )}
                                </p>
                            </div>
                        </div>

                        <div className={styles.tableContainer}>
                            <Table
                                columns={programItemColumns}
                                data={flattenedProgramItems}
                                tableLayout="auto"
                            />
                        </div>
                    </>
                )
            )}
        </div>
    );
};

export default PreviewWorkout;
